// @ts-check
import { gql } from '@apollo/client';
import { Document } from '@react-pdf/renderer';
import React from 'react';

/**
 * @template {{ [x: string]: any; }} Props
 * @param {React.ComponentType<Props>
 * & {
 *  fragment: import('graphql').DocumentNode;
 *  beforeRender?:  import('./Preview').BeforeRenderEvent;
 *  afterRender?:  import('./Preview').AfterRenderEvent;
 * }} CatalogSection
 */
export default function asCatalogSections(CatalogSection) {
  return {
    beforeQuery,
    beforeRender: CatalogSection.beforeRender,
    query: getQuery(CatalogSection.fragment),
    layout: (/** @type {Props} */ { data, ...rest }) => {
      const { collection, sections } = afterQuery(data);
      const catalogContext = new BottlebooksCatalogContext();
      return (
        <Document
          onRender={CatalogSection.afterRender}
          title={`${collection?.name} Catalog`}
        >
          {sections?.map(
            (/** @type {object} */ section, /** @type {React.Key} */ index) => {
              return (
                <CatalogSection
                  key={index}
                  data={section}
                  catalogContext={catalogContext}
                  {...rest}
                />
              );
            }
          )}
        </Document>
      );
    },
  };
}

/**
 * @returns
 */
function BottlebooksCatalogContext() {
  let pageNumber = 0;
  let sectionPageNumber = 0;
  let sectionNumber = 0;

  this.incrementPage = () => {
    pageNumber++;
    sectionPageNumber++;
  };
  this.incrementSection = () => {
    sectionNumber++;
    sectionPageNumber = 0;
  };

  this.getPageNumber = () => {
    return pageNumber;
  };

  this.getSectionPageNumber = () => {
    return sectionPageNumber;
  };

  this.getSectionNumber = () => {
    return sectionNumber;
  };
}

/**
 * Creates the query variables.
 * @param {object} props
 * @param {string} props.collectionId The id of the collection.
 * @param {import('@bottlebooks/graphql-server/src').ContentLocale} [props.locale] The locale - en by default.
 * @param {string} [props.companyId] The ID of the company to preview the section for.
 * @param {import('@bottlebooks/graphql-server/src').CatalogSectionPositionFilterInput} [props.position] The filter on the position. Set to `{}` to allow any
 * @returns {import('@apollo/client').QueryHookOptions<any, {
 *  collectionId: string;
 *  locale: import('@bottlebooks/graphql-server/src').ContentLocale;
 *  sectionFilter?: import('@bottlebooks/graphql-server/src').CatalogSectionFilterInput;
 * }>}
 */
function beforeQuery({
  collectionId,
  locale = 'en',
  companyId,
  position = { eq: 'MANUAL' },
}) {
  return {
    variables: {
      collectionId,
      locale,
      sectionFilter: {
        sectionType: { eq: 'REGISTRATION' },
        registration: companyId ? { companyId: { eq: companyId } } : undefined,
        position,
      },
    },
  };
}

/**
 *
 * Creates a query from a fragment.
 * @param {import('graphql').DocumentNode} fragment
 */
function getQuery(fragment) {
  const firstDefinition = fragment.definitions[0];
  if (firstDefinition?.kind !== 'FragmentDefinition')
    throw new Error('You need to pass a fragment!');
  const onType = firstDefinition.typeCondition.name.value;
  const parse = gql; // Renamed to avoid IDE errors
  if (!['CatalogSection', 'RegistrationCatalogSection'].includes(onType))
    throw new Error('You must pass a fragment on CatalogSection');
  return parse/* GraphQL */ `
    query Catalog_${firstDefinition.name.value}(
      $collectionId: ID!
      $locale: ContentLocale!
      $sectionFilter: CatalogSectionFilterInput! # used in CaliforniaTableSign
    ) {
      collection(
        collectionId: $collectionId
        locale: $locale
      ) {
        ... on Event {
          eventId # Needed for apollo-client.
        }
        collectionId
        name
        catalog {
          # Variables are defined in query.js
          sections(filter: $sectionFilter) {
            sectionType
              ...${firstDefinition.name.value}
          }
        }
      }
    }

    ${fragment}
  `;
}

function afterQuery(data) {
  return {
    collection: data?.collection,
    sections: data?.collection?.catalog?.sections,
  };
}
